import { A, Container, H1, H2, H3, Ol, P, Strong } from '#app/ui/components'

export default function PurchaseConditions() {
	return (
		<Container className="py-16">
			<div className="mx-auto max-w-3xl space-y-8">
				<H1>Ticket Purchase Conditions</H1>

				<Ol>
					<li className="space-y-4">
						<H2>Identification and Contact</H2>
						<P>
							The web platform{` `}
							<A
								href="https://welovenight.com/"
								target="_blank"
								rel="noopener noreferrer"
							>
								https://welovenight.com/
							</A>{` `}
							(hereinafter, the "Platform") is owned by WE LOVE NIGHT S.L., with
							Tax ID B75524363, with registered office at Calle Miquel Marqués
							25, 07005 Palma de Mallorca (Illes Balears), Spain, and its
							contact email address is bielmartinez@welovenight.com
							(hereinafter, "WELOVENIGHT" or the "Company").
						</P>
						<P>
							For any enquiry, complaint or request for information, users may
							contact WELOVENIGHT through:
						</P>
						<div className="space-y-2">
							<P className="mb-0">
								<Strong>Email:</Strong>{` `}
								<A href="mailto:bielmartinez@welovenight.com">
									bielmartinez@welovenight.com
								</A>
							</P>
							<P className="mb-0">
								<Strong>Telephone:</Strong> +34 628 38 60 38
							</P>
							<P>
								<Strong>Postal address:</Strong> Calle Miquel Marqués, 25, 07005
								Palma de Mallorca (Illes Balears), Spain
							</P>
						</div>
					</li>

					<li className="space-y-4">
						<H2>Purpose and Scope of Application</H2>
						<P>
							These Ticket Purchase Conditions (hereinafter, the "Purchase
							Conditions") establish the conditions governing the purchase of
							tickets for events through the Platform. Together with the{` `}
							<A href="/registration-conditions">
								Registration and User Account Conditions
							</A>
							, <A href="/legal-notice">Legal Notice</A>,{` `}
							<A href="/privacy-policy">Privacy Policy</A> and Cookie Policy, they form
							the applicable legal framework (hereinafter, the "Legal Terms of
							the Platform") for the use of the Platform.
						</P>
						<P>
							By making a purchase through the Platform (hereinafter,
							"Purchase"), they acquire the status of Customer (hereinafter,
							"Customer"). Making a Purchase and its confirmation implies the
							Customer's full and complete acceptance of these Purchase
							Conditions, as well as the Customer's acceptance of the rest of
							the Legal Terms of the Platform.
						</P>
						<P>
							As a Customer, the Customer acknowledges that they are aware, at
							the time of making the Purchase of the particular conditions of
							sale indicated for each Event published on the Platform and
							expressly declare that they accept them without reservation.
						</P>
					</li>

					<li>
						<H2>Ticket Sales Service</H2>
						<Ol>
							<li className="space-y-4">
								<H3>Nature of the service</H3>
								<P>
									WELOVENIGHT provides Users with an advance digital ticket
									sales service for leisure and nightlife events, including
									nightclubs, afternoon parties, themed parties and other
									similar events (hereinafter, the "Events").
								</P>
								<P>
									For the purposes of these Purchase Conditions, the event
									organiser ("Event Organiser") means the natural or legal
									person responsible for organizing, promoting, and producing an
									Event, who has decision-making authority over all aspects of
									the Event including but not limited to venue selection,
									programming, content, security arrangements, operational
									logistics, and Event conditions.
								</P>
								<P>
									WELOVENIGHT facilitates the Customer's acquisition of tickets
									and manages their distribution on behalf and for the account
									of the Event Organiser, although WELOVENIGHT may sometimes act
									as the Event Organiser itself.
								</P>
								<P>
									When WELOVENIGHT is not acting as the Event Organiser,
									WELOVENIGHT is expressly exempt from all obligation and/or
									responsibility corresponding to the Event Organiser for the
									organisational, logistical and/or configurational elements of
									the Event over which it has no decision-making capacity, such
									as visibility, comfort, acoustics, accessibility, security
									conditions, etc.
								</P>
							</li>

							<li className="space-y-4">
								<H3>WELOVENIGHT's role</H3>
								<P>WELOVENIGHT acts as:</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											Intermediary between Customers and Event Organisers for
											ticket sales.
										</P>
									</li>
									<li>
										<P>
											Payment processor, facilitating secure payment through
											authorised payment gateways.
										</P>
									</li>
									<li>
										<P>
											Ticket distributor, issuing digital tickets with QR codes
											for event access.
										</P>
									</li>
									<li>
										<P>
											Platform provider, maintaining the technical
											infrastructure for ticket sales.
										</P>
									</li>
									<li>
										<P>
											Event Organiser, on occasion, where expressly indicated on
											the Platform, WELOVENIGHT may act as the Event Organiser.
										</P>
									</li>
								</Ol>

								<P>
									Where WELOVENIGHT does not act as the Event Organiser, it does
									not:
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>Organise, promote or produce Events.</P>
									</li>
									<li>
										<P>Control Event content, quality or characteristics.</P>
									</li>
									<li>
										<P>Guarantee Event performance or Customer satisfaction.</P>
									</li>
									<li>
										<P>
											Assume responsibility for Organiser acts or omissions.
										</P>
									</li>
								</Ol>
							</li>

							<li className="space-y-4">
								<H3>Event information</H3>
								<P>
									The availability of Events offered will be subject to what is
									established on the Platform at the time of Purchase. Customers
									can find detailed information in the description of each Event
									on the Platform, including:
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>Event name and description.</P>
									</li>
									<li>
										<P>Date, time and location.</P>
									</li>
									<li>
										<P>Organiser information.</P>
									</li>
									<li>
										<P>Ticket types and prices.</P>
									</li>
									<li>
										<P>Age restrictions.</P>
									</li>
									<li>
										<P>Dress code or special requirements.</P>
									</li>
									<li>
										<P>Specific Event rules.</P>
									</li>
									<li>
										<P>Refund and cancellation policy.</P>
									</li>
									<li>
										<P>Capacity limitations.</P>
									</li>
								</Ol>
								<P>
									All Events are subject to availability at that time and there
									may be additional requirements for some of them, which
									WELOVENIGHT will inform Customers of in good time before
									purchase.
								</P>
								<P>
									WELOVENIGHT is not responsible for the accuracy or
									completeness of Event information provided by the Event
									Organisers. Customers should verify Event details before
									making a Purchase.
								</P>
							</li>
						</Ol>
					</li>

					<li className="space-y-4">
						<H2>Purchase Process</H2>
						<Ol>
							<li className="space-y-4">
								<H3>Purchase methods and account requirements</H3>
								<P>Customers may purchase tickets through different methods:</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											<Strong>Purchase with registered account:</Strong> Have a
											registered account on the Platform and be logged into
											their account (see{` `}
											<A href="/registration-conditions">
												Registration and User Account Conditions
											</A>
											).
										</P>
									</li>
									<li>
										<P>
											<Strong>Purchase without registered account:</Strong>{` `}
											Provide a valid email address for ticket delivery.
										</P>
									</li>
								</Ol>
								<P>To complete a purchase, Customers must:</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<P>Be of legal age (18 years or older).</P>
									<li></li>
									<li>
										<P>Have a valid payment method accepted by the Platform.</P>
									</li>
									<li>
										<P>Accept these Purchase Conditions.</P>
									</li>
								</Ol>
								<P>
									<Strong>Ticket association with accounts:</Strong>
								</P>
								<P>
									If the email address used for purchase corresponds to an
									existing registered account, the ticket will be automatically
									associated with that account.
								</P>
								<P>
									If the Customer creates an account after purchase using the
									same email address, the ticket will be automatically
									associated with the newly created account.
								</P>
								<P>
									If no account is created, the ticket will be delivered via
									email with a QR code for Event access. Tickets delivered via
									email function identically to tickets associated with
									accounts.
								</P>
							</li>

							<li className="space-y-4">
								<H3>Selection and purchase of tickets</H3>
								<P>
									To make a Purchase, the Customer must follow the Purchase
									procedure that WELOVENIGHT will indicate through the Platform.
								</P>
								<P>
									<Strong>Step 1: Event selection</Strong>
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>Browse available Events.</P>
									</li>
									<li>
										<P>Select the Event the Customer wishes to attend.</P>
									</li>
									<li>
										<P>
											Review Event details, date, time, location and conditions.
										</P>
									</li>
								</Ol>

								<P>
									<Strong>Step 2: Ticket selection</Strong>
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>Select ticket type (if multiple types available).</P>
									</li>
									<li>
										<P>Select quantity of tickets.</P>
									</li>
									<li>
										<P>Review price per ticket and total price.</P>
									</li>
									<li>
										<P>Check availability.</P>
									</li>
								</Ol>

								<P>
									<Strong>Step 3: Review and confirmation</Strong>
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>Review their selection in the shopping basket.</P>
									</li>
									<li>
										<P>Verify Event details, quantity and total price.</P>
									</li>
									<li>
										<P>Apply promotional codes if available.</P>
									</li>
									<li>
										<P>Review and accept specific Event conditions.</P>
									</li>
								</Ol>

								<P>
									<Strong>Step 4: Payment</Strong>
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>Select payment method.</P>
									</li>
									<li>
										<P>Enter payment details (processed securely by Stripe).</P>
									</li>
									<li>
										<P>Confirm purchase.</P>
									</li>
								</Ol>

								<P>
									<Strong>Step 5: Confirmation</Strong>
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>Receive on-screen confirmation.</P>
									</li>
									<li>
										<P>Access tickets in the Customer's account.</P>
									</li>
								</Ol>
							</li>

							<li className="space-y-4">
								<H3>Non-nominative tickets</H3>
								<P>
									Tickets are associated with the person making the Purchase but
									are not nominative. The Customer can purchase as many tickets
									as they wish for the same Event, without it being necessary to
									identify each individual attendee.
								</P>
								<P>However:</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											The Customer is responsible for all tickets purchased.
										</P>
									</li>
									<li>
										<P>
											Tickets may be subject to maximum purchase limits per
											person or per credit card.
										</P>
									</li>
									<li>
										<P>
											The Organiser may request identification at the Event
											entrance.
										</P>
									</li>
									<li>
										<P>Tickets may not be resold illegally.</P>
									</li>
								</Ol>
							</li>

							<li className="space-y-4">
								<H3>Capacity limit</H3>
								<P>
									There is a limit of available tickets for each Event
									determined by the venue's capacity.
								</P>
								<P>The Platform will indicate:</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>Whether tickets are available or sold out.</P>
									</li>
									<li>
										<P>
											When capacity is close to being completed (approximately
											20% of remaining capacity), "few tickets remain".
										</P>
									</li>
								</Ol>
								<P>
									Tickets may be restricted to a maximum number per person, per
									credit card and, for some Events, per family, as indicated in
									each Event. This policy seeks to prevent unfair practices in
									ticket Purchase, such as bulk buying for illegal resale.
								</P>
							</li>

							<li className="space-y-4">
								<H3>Errors in the Purchase process</H3>
								<P>
									The Platform has confirmation windows in each section of the
									Purchase process. The Platform offers details of all items the
									Customer has added to the shopping basket, so that, before
									making payment, the Customer can check and, where appropriate,
									modify the order details.
								</P>
								<P>
									If the Customer detects errors in the order after completion
									of the Purchase process, the Customer must contact WELOVENIGHT
									immediately at bielmartinez@welovenight.com, to rectify the
									error before Purchase processing begins.
								</P>
								<P>
									WELOVENIGHT is not responsible for errors during Purchase
									processing caused by:
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>Incorrect information provided by the Customer.</P>
									</li>
									<li>
										<P>Technical issues on the Customer's device.</P>
									</li>
									<li>
										<P>Internet connection problems.</P>
									</li>
									<li>
										<P>
											Customer's failure to review order details before
											confirmation.
										</P>
									</li>
								</Ol>
								<P>
									WELOVENIGHT is not responsible for possible deficiencies
									caused by the Customer not having the technical requirements
									on their device that allow correct Platform operation and
									normal execution of the Purchase process.
								</P>
							</li>
						</Ol>
					</li>

					<li className="space-y-4">
						<H2>Price and Payment Method</H2>
						<Ol>
							<li className="space-y-4">
								<H3>Price</H3>
								<P>
									Ticket prices are those shown on the Platform at the time the
									Customer places the Purchase, including applicable taxes in
									each case.
								</P>
								<Ol>
									<li>
										<P>
											<Strong>Price information:</Strong>
										</P>
										<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
											<li>
												<P>Prices are expressed in euros (€).</P>
											</li>
											<li>
												<P>
													Prices include Spanish VAT at the applicable rate.
												</P>
											</li>
											<li>
												<P>
													Prices may include booking fees or service charges
													(clearly indicated).
												</P>
											</li>
											<li>
												<P>Prices are per ticket unless otherwise stated.</P>
											</li>
										</Ol>
									</li>

									<li>
										<P>
											<Strong>Price changes:</Strong>
										</P>
										<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
											<li>
												<P>
													Prices may change at any time without prior notice.
												</P>
											</li>
											<li>
												<P>
													Price changes will not affect orders for which a
													Purchase confirmation has already been sent.
												</P>
											</li>
											<li>
												<P>
													The price the Customer pays is the price displayed at
													the time of order confirmation.
												</P>
											</li>
										</Ol>
									</li>

									<li>
										<P>
											<Strong>Promotional codes and discounts:</Strong>
										</P>
										<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
											<li>
												<P>
													Promotional codes may be applied during the purchase
													process.
												</P>
											</li>
											<li>
												<P>Discounts will be clearly shown before payment.</P>
											</li>
											<li>
												<P>
													Promotional codes are subject to specific terms and
													conditions.
												</P>
											</li>
											<li>
												<P>
													Promotional codes may have expiry dates or usage
													limitations.
												</P>
											</li>
										</Ol>
									</li>
								</Ol>
							</li>

							<li className="space-y-4">
								<H3>Payment method: Stripe</H3>
								<P>
									Payment for tickets is made exclusively through the Stripe
									payment gateway.
								</P>
								<Ol>
									<li>
										<P>
											<Strong>Accepted payment methods:</Strong>
										</P>
										<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
											<li>
												<P>
													Credit cards (Visa, Mastercard, American Express,
													etc.).
												</P>
											</li>
											<li>
												<P>Debit cards.</P>
											</li>
											<li>
												<P>
													Other payment methods available through Stripe (as
													indicated on the Platform).
												</P>
											</li>
										</Ol>
									</li>

									<li>
										<P>
											<Strong>Payment security:</Strong>
										</P>
										<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
											<li>
												<P>All payments are processed through secure ports.</P>
											</li>
											<li>
												<P>
													Payment data travels encrypted through Stripe's secure
													payment platforms.
												</P>
											</li>
											<li>
												<P>Payment data is not stored by WELOVENIGHT.</P>
											</li>
											<li>
												<P>
													The Platform has an SSL certificate to protect banking
													information.
												</P>
											</li>
											<li>
												<P>
													Confidential credit or debit card payment data is
													transmitted directly and securely to the financial
													institution.
												</P>
											</li>
										</Ol>
									</li>

									<li>
										<P>
											<Strong>Payment authorisation:</Strong>
										</P>
										<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
											<li>
												<P>
													WELOVENIGHT reserves the right to suspend Purchase
													processing in case of rejection of payment
													authorisation by the bank or payment entity.
												</P>
											</li>
											<li>
												<P>
													If payment is declined, the Customer will be notified
													and may attempt payment again.
												</P>
											</li>
											<li>
												<P>
													No tickets will be issued until payment is
													successfully authorised.
												</P>
											</li>
										</Ol>
									</li>

									<li>
										<P>
											<Strong>Payment confirmation:</Strong>
										</P>
										<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
											<li>
												<P>
													The Customer will receive immediate on-screen
													confirmation of successful payment.
												</P>
											</li>
											<li>
												<P>
													A confirmation email will be sent with payment
													details.
												</P>
											</li>
											<li>
												<P>
													The charge will appear on the Customer's bank
													statement.
												</P>
											</li>
										</Ol>
									</li>

									<li>
										<P>
											<Strong>Refunds:</Strong>
										</P>
										<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
											<li>
												<P>
													Refunds (when applicable) will be processed through
													the same payment method used for purchase.
												</P>
											</li>
											<li>
												<P>
													Refunds are subject to the Refund and Reimbursement
													Policy (see clause 7).
												</P>
											</li>
										</Ol>
									</li>
								</Ol>
							</li>

							<li className="space-y-4">
								<H3>Invoicing</H3>
								<P>
									WELOVENIGHT will issue an invoice in electronic format
									whenever the Customer expressly requests it at
									bielmartinez@welovenight.com.
								</P>
								<Ol>
									<li>
										<P>
											<Strong>Invoice details:</Strong>
										</P>
										<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
											<li>
												<P>
													The invoice will be issued in the name of the natural
													or legal person placing the Purchase.
												</P>
											</li>
											<li>
												<P>
													The Customer must ensure they enter correct and
													complete details.
												</P>
											</li>
											<li>
												<P>Subsequent changes will not be possible.</P>
											</li>
											<li>
												<P>
													For legal entities, tax identification number and
													complete details must be provided.
												</P>
											</li>
										</Ol>
									</li>
									<li>
										<P>
											<Strong>Invoice request:</Strong>
										</P>
										<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
											<li>
												<P>
													Invoices must be requested within 30 days of the
													Purchase.
												</P>
											</li>
											<li>
												<P>
													Requests must include order number, full name/company
													name, tax ID, complete address.
												</P>
											</li>
											<li>
												<P>Invoices will be sent by email in PDF format.</P>
											</li>
										</Ol>
									</li>
									<li>
										<P>
											<Strong>VAT:</Strong>
										</P>
										<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
											<li>
												<P>
													Spanish VAT will be applied at the rate applicable to
													the Event.
												</P>
											</li>
											<li>
												<P>VAT will be itemised separately on the invoice.</P>
											</li>
										</Ol>
									</li>
								</Ol>
							</li>
						</Ol>
					</li>

					<li className="space-y-4">
						<H2>Digital Tickets and QR Codes</H2>
						<Ol>
							<li className="space-y-4">
								<H3>Digital ticket format</H3>
								<P>
									Tickets purchased are digital and are presented through a
									unique and non-transferable QR code that must be shown at the
									time of access to the Event.
								</P>
								<Ol>
									<li>
										<P>
											<Strong>Ticket characteristics:</Strong>
										</P>
										<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
											<li>
												<P>Each ticket contains a unique QR code.</P>
											</li>
											<li>
												<P>
													Tickets are delivered electronically (account access).
												</P>
											</li>
											<li>
												<P>
													Tickets can be displayed on mobile devices or printed.
												</P>
											</li>
											<li>
												<P>Each QR code can only be used once.</P>
											</li>
										</Ol>
									</li>
								</Ol>
							</li>

							<li className="space-y-4">
								<H3>Accessing tickets</H3>
								<P>
									After Purchase confirmation, the Customer can access their
									tickets through:
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											The Customer's account (for registered accounts): Log into
											their account and access "My Tickets" or similar section.
										</P>
									</li>
									<li>
										<P>
											Email delivery: Tickets will be sent to the email address
											provided during purchase.
										</P>
									</li>
									<li>
										<P>
											Mobile app: (When available) Access tickets through the
											WELOVENIGHT mobile application.
										</P>
									</li>
								</Ol>

								<P>
									<Strong>Recommendations:</Strong>
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											Customers should save tickets to their mobile device for
											offline access.
										</P>
									</li>
									<li>
										<P>
											Customers should take a screenshot of the QR code as
											backup.
										</P>
									</li>
									<li>
										<P>
											Customers should print tickets if they prefer physical
											copies.
										</P>
									</li>
									<li>
										<P>
											Customers should ensure their mobile device is charged
											before the Event.
										</P>
									</li>
								</Ol>
							</li>

							<li className="space-y-4">
								<H3>Lost or stolen tickets</H3>
								<P>
									WELOVENIGHT assumes no responsibility for any lost or stolen
									ticket. If the Customer loses access to their tickets:
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											Customers should log in to their account to access tickets
											again (if purchased with an account).
										</P>
									</li>
									<li>
										<P>Customers should check their confirmation email.</P>
									</li>
									<li>
										<P>
											Customers should contact customer service at
											bielmartinez@welovenight.com with their order number.
										</P>
									</li>
								</Ol>

								<P>
									<Strong>Important:</Strong>
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											It is the Customer's responsibility to safeguard tickets.
										</P>
									</li>
									<li>
										<P>
											WELOVENIGHT cannot reissue tickets if the QR code has been
											compromised.
										</P>
									</li>
									<li>
										<P>
											If someone else uses the Customer's QR code before the
											Customer, the Customer may be denied entry.
										</P>
									</li>
									<li>
										<P>
											WELOVENIGHT is not responsible for tickets not purchased
											through the official Platform.
										</P>
									</li>
								</Ol>
							</li>

							<li className="space-y-4">
								<H3>Ticket validation system</H3>
								<P>The Platform records information about:</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>Ticket purchase times.</P>
									</li>
									<li>
										<P>
											Ticket validation times (when scanned at Event entrance).
										</P>
									</li>
									<li>
										<P>Number of validation attempts.</P>
									</li>
								</Ol>

								<P>This information is used for:</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>Preventing fraud and duplicate entry.</P>
									</li>
									<li>
										<P>Generating attendance statistics for Organisers.</P>
									</li>
									<li>
										<P>Improving service quality.</P>
									</li>
									<li>
										<P>Analysing PR campaign performance.</P>
									</li>
								</Ol>
							</li>
						</Ol>
					</li>

					<li className="space-y-4">
						<H2>Refund and Reimbursement Policy</H2>
						<Ol>
							<li className="space-y-4">
								<H3>General refund policy</H3>
								<P>
									Tickets purchased are only refunded upon specific request from
									the Customer. Ticket sales do not admit changes or refunds
									automatically, except in the following cases:
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>Cancellation of the Event by the Organiser.</P>
									</li>
									<li>
										<P>Substantial alteration in the Event.</P>
									</li>
									<li>
										<P>Cases provided for in current legislation.</P>
									</li>
									<li>
										<P>
											Exceptional circumstances at WELOVENIGHT's discretion.
										</P>
									</li>
								</Ol>

								<P>The following are NOT grounds for automatic refund:</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>Inability to attend the Event.</P>
									</li>
									<li>
										<P>
											Error when making the purchase (Customer's responsibility
											to review before confirming).
										</P>
									</li>
									<li>
										<P>Change of mind.</P>
									</li>
									<li>
										<P>Personal circumstances preventing attendance.</P>
									</li>
									<li>
										<P>
											Dissatisfaction with the Event (responsibility of the
											Organiser).
										</P>
									</li>
								</Ol>
							</li>

							<li className="space-y-4">
								<H3>Requesting a refund</H3>
								<P>
									Customers wishing to request refund of their tickets must:
								</P>
								<P>
									<Strong>Step 1: Send a written request</Strong>
								</P>
								<P>
									Email:{` `}
									<A href="mailto:bielmartinez@welovenight.com">
										bielmartinez@welovenight.com
									</A>
									.
								</P>
								<P>Include:</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>Purchase number.</P>
									</li>
									<li>
										<P>Customer's full name.</P>
									</li>
									<li>
										<P>Email address associated with the account.</P>
									</li>
									<li>
										<P>Event name and date.</P>
									</li>
									<li>
										<P>Number of tickets to return.</P>
									</li>
									<li>
										<P>Detailed reason for refund request.</P>
									</li>
									<li>
										<P>Proof of exceptional circumstances (if applicable).</P>
									</li>
								</Ol>

								<P>
									<Strong>Step 2: Timing</Strong>
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											Requests must be made with a minimum advance notice of 48
											hours before the Event starts.
										</P>
									</li>
									<li>
										<P>
											Requests made after this deadline will not be considered.
										</P>
									</li>
									<li>
										<P>
											Requests made after the Event has taken place will not be
											accepted.
										</P>
									</li>
								</Ol>

								<P>
									<Strong>Step 3: Ticket status</Strong>
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											Tickets must not have been validated (scanned) at the
											Event entrance.
										</P>
									</li>
									<li>
										<P>Once a ticket is validated, no refund is possible.</P>
									</li>
								</Ol>
							</li>

							<li className="space-y-4">
								<H3>Refund evaluation and processing</H3>
								<Ol>
									<li>
										<P>
											<Strong>Evaluation:</Strong>
										</P>
										<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
											<li>
												<P>
													WELOVENIGHT will study each refund request
													individually.
												</P>
											</li>
											<li>
												<P>
													WELOVENIGHT reserves the right to accept or reject the
													refund according to the specific circumstances of each
													case.
												</P>
											</li>
											<li>
												<P>
													WELOVENIGHT may request additional documentation or
													information.
												</P>
											</li>
											<li>
												<P>
													The decision will be communicated by email within 5
													working days.
												</P>
											</li>
										</Ol>
									</li>
									<li>
										<P>
											<Strong>If the refund is accepted:</Strong>
										</P>
										<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
											<li>
												<P>Refund amount:</P>
												<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-roman)')'] [&>li]:pl-2">
													<li>
														<P>Full ticket price, or</P>
													</li>
													<li>
														<P>
															Ticket price minus a management commission of 20%
															per ticket.
														</P>
													</li>
													<li>
														<P>
															The refund amount will be clearly communicated in
															the acceptance email.
														</P>
													</li>
												</Ol>
											</li>

											<li>
												<P>Refund method:</P>
												<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-roman)')'] [&>li]:pl-2">
													<li>
														<P>
															Refund will be made through the same payment
															method used in the original purchase.
														</P>
													</li>
													<li>
														<P>Refunds are processed through Stripe.</P>
													</li>
												</Ol>
											</li>

											<li>
												<P>Refund timing:</P>
												<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-roman)')'] [&>li]:pl-2">
													<li>
														<P>
															The refund will be initiated as soon as possible
															but always within 3 to 15 working days following
															acceptance of the request.
														</P>
													</li>
													<li>
														<P>
															Depending on the Customer's bank, it may take
															additional days for the refund to appear in their
															account.
														</P>
													</li>
													<li>
														<P>
															The Customer will receive email confirmation when
															the refund is processed.
														</P>
													</li>
												</Ol>
											</li>
										</Ol>
									</li>
									<li>
										<P>
											<Strong>If the refund is rejected:</Strong>
										</P>
										<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
											<li>
												<P>
													The Customer will receive an email explaining the
													reasons for rejection.
												</P>
											</li>
											<li>
												<P>
													The Customer's tickets will remain valid for the
													Event.
												</P>
											</li>
											<li>
												<P>
													The Customer may transfer the tickets to another
													person (subject to Event rules).
												</P>
											</li>
										</Ol>
									</li>
								</Ol>
							</li>

							<li className="space-y-4">
								<H3>Event cancellation by the Organiser</H3>
								<P>
									In case of Event cancellation by the Organiser (and the date
									is not changed), refund of the ticket sale price will be
									offered, according to what the Organiser establishes.
								</P>
								<P>
									<Strong>Notification:</Strong>
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											WELOVENIGHT will notify all ticket holders by email as
											soon as possible after receiving cancellation notice from
											the Organiser.
										</P>
									</li>
									<li>
										<P>
											The notification will include refund instructions and
											deadlines.
										</P>
									</li>
								</Ol>

								<P>
									<Strong>Refund process:</Strong>
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											Where WELOVENIGHT does not act as the Event Organiser,
											WELOVENIGHT, as distributor, may only proceed to cancel
											tickets and refund their amount when it receives express
											order from the Event Organiser.
										</P>
									</li>
									<li>
										<P>Refunds will be automatic (no request required).</P>
									</li>
									<li>
										<P>
											The full ticket price will be refunded (no management
											commission).
										</P>
									</li>
									<li>
										<P>
											The refund will be automatically executed to the card from
											which the Purchase was made within an approximate period
											of 3 to 15 working days.
										</P>
									</li>
								</Ol>

								<P>
									<Strong>WELOVENIGHT's liability:</Strong>
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											WELOVENIGHT follows the Event Organiser's indications
											regarding refund management.
										</P>
									</li>
									<li>
										<P>
											If WELOVENIGHT does not act as Event Organiser,
											WELOVENIGHT is not responsible for Event cancellation.
										</P>
									</li>
									<li>
										<P>
											WELOVENIGHT's obligation is limited to processing refunds
											as instructed by the Organiser.
										</P>
									</li>
								</Ol>
							</li>

							<li className="space-y-4">
								<H3>Event date change</H3>
								<P>
									Unless otherwise indicated by the Event Organiser, if the
									Event date is changed:
								</P>
								<P>
									<Strong>Option 1: Attend on the new date</Strong>
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											The Customer's tickets will automatically be valid for the
											new date.
										</P>
									</li>
									<li>
										<P>
											Tickets will be of a value corresponding to the original
											tickets.
										</P>
									</li>
									<li>
										<P>
											No action is required if the Customer can attend on the
											new date.
										</P>
									</li>
								</Ol>

								<P>
									<Strong>Option 2: Request a refund</Strong>
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											If the Customer cannot attend the Event on the new date,
											the Customer may request a refund.
										</P>
									</li>
									<li>
										<P>
											The Customer must inform WELOVENIGHT within the deadline
											specified in the date change notification.
										</P>
									</li>
									<li>
										<P>
											Refund will be processed as per clause 7.4 (full refund,
											no commission).
										</P>
									</li>
								</Ol>

								<P>
									<Strong>Failure to respond:</Strong>
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											If the Customer does not inform WELOVENIGHT within the
											specified deadline, WELOVENIGHT may confirm the Customer's
											booking for the new date.
										</P>
									</li>
									<li>
										<P>
											After confirmation, the Customer will not have the right
											to demand a refund.
										</P>
									</li>
								</Ol>

								<P>
									<Strong>Substantial changes:</Strong>
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											If the new date is significantly different (e.g. more than
											3 months from the original date), automatic refund rights
											may apply.
										</P>
									</li>
									<li>
										<P>
											WELOVENIGHT will communicate specific conditions in the
											date change notification.
										</P>
									</li>
								</Ol>
							</li>

							<li className="space-y-4">
								<H3>Substantial alteration of the Event</H3>
								<P>
									If the Event suffers substantial alterations that
									significantly affect its nature or value, Customers may be
									entitled to a refund.
								</P>
								<P>
									<Strong>Examples of substantial alterations:</Strong>
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>Change of main artist/performer.</P>
									</li>
									<li>
										<P>Significant reduction in Event duration.</P>
									</li>
									<li>
										<P>
											Change of venue to a substantially different location.
										</P>
									</li>
									<li>
										<P>Significant change in Event format or content.</P>
									</li>
								</Ol>

								<P>
									<Strong>Process:</Strong>
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											WELOVENIGHT will evaluate whether the alteration is
											substantial.
										</P>
									</li>
									<li>
										<P>
											If deemed substantial, affected Customers will be notified
											and offered a refund.
										</P>
									</li>
									<li>
										<P>
											Customers must request the refund within the specified
											deadline.
										</P>
									</li>
								</Ol>
							</li>
						</Ol>
					</li>

					<li className="space-y-4">
						<H2>Ticket Validation and Event Access</H2>
						<Ol>
							<li className="space-y-4">
								<H3>Presenting your ticket</H3>
								<P>
									Once the Customer has purchased their ticket, they must show
									it at the Event access point to gain entry. How the Customer
									should present the ticket:
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											The Customer must display the QR code on their mobile
											device or present a printed copy of the ticket.
										</P>
									</li>
									<li>
										<P>
											They must ensure the QR code is clearly visible and not
											damaged.
										</P>
									</li>
									<li>
										<P>
											They should have the QR code ready before reaching the
											entrance to avoid delays.
										</P>
									</li>
								</Ol>
							</li>
						</Ol>
					</li>

					<li className="space-y-4">
						<H2>Limitation of Liability</H2>
						<Ol>
							<li className="space-y-4">
								<H3>General limitation</H3>
								<P>
									To the maximum extent permitted by applicable law,
									WELOVENIGHT's total liability to any Customer for all claims
									arising out of or relating to the purchase of tickets, or
									these Purchase Conditions, whether in contract, tort, or
									otherwise, shall not exceed the total amount paid by the
									Customer for the specific tickets giving rise to the claim.
								</P>
							</li>

							<li className="space-y-4">
								<H3>Exclusion of consequential damages</H3>
								<P>
									To the maximum extent permitted by applicable law, WELOVENIGHT
									shall not be liable for any indirect, incidental, special,
									consequential, or punitive damages, including but not limited
									to loss of profits, loss of data, loss of use, loss of
									goodwill, or other intangible losses, regardless of whether
									WELOVENIGHT has been advised of the possibility of such
									damages.
								</P>
							</li>

							<li className="space-y-4">
								<H3>Event-related limitations</H3>
								<P>
									When WELOVENIGHT does not act as the Event Organiser,
									WELOVENIGHT shall not be liable for:
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											Event cancellation, postponement, or modification by the
											Organiser.
										</P>
									</li>
									<li>
										<P>Quality, content, or characteristics of the Event.</P>
									</li>
									<li>
										<P>
											Venue conditions, including but not limited to acoustics,
											visibility, comfort, accessibility, or security.
										</P>
									</li>
									<li>
										<P>
											Acts or omissions of the Event Organiser, venue operators,
											artists, or other third parties.
										</P>
									</li>
									<li>
										<P>
											Personal injury, property damage, or any other harm
											occurring at the Event.
										</P>
									</li>
									<li>
										<P>Customer dissatisfaction with the Event experience.</P>
									</li>
								</Ol>
							</li>

							<li className="space-y-4">
								<H3>Force majeure and unforeseeable circumstances</H3>
								<P>
									WELOVENIGHT shall not be liable for any failure to perform its
									obligations under these Purchase Conditions if such failure
									results from circumstances beyond its reasonable control,
									including but not limited to:
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											Acts of God, natural disasters, epidemics, or pandemics.
										</P>
									</li>
									<li>
										<P>
											Government actions, laws, regulations, or court orders.
										</P>
									</li>
									<li>
										<P>War, terrorism, civil unrest, or labour disputes.</P>
									</li>
									<li>
										<P>
											Failure of third-party service providers, including
											payment processors and technology providers.
										</P>
									</li>
									<li>
										<P>
											Cyber attacks, system failures, or telecommunications
											failures.
										</P>
									</li>
								</Ol>
							</li>

							<li className="space-y-4">
								<H3>Customer obligations and assumption of risk</H3>
								<P>
									By purchasing tickets through the Platform, Customers
									acknowledge and assume the following risks and
									responsibilities:
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											Responsibility for safeguarding tickets and preventing
											unauthorised access.
										</P>
									</li>
									<li>
										<P>
											Risk of Event cancellation, modification, or postponement
											by the Organiser.
										</P>
									</li>
									<li>
										<P>
											Responsibility for ensuring compatibility of their devices
											with the Platform.
										</P>
									</li>
									<li>
										<P>Risk of personal injury or property damage at Events.</P>
									</li>
									<li>
										<P>
											Responsibility for compliance with Event rules and venue
											requirements.
										</P>
									</li>
								</Ol>
							</li>

							<li className="space-y-4">
								<H3>Statutory rights preservation</H3>
								<P>
									Nothing in this limitation of liability clause shall exclude
									or limit WELOVENIGHT's liability for:
								</P>
								<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
									<li>
										<P>
											Death or personal injury caused by WELOVENIGHT's
											negligence.
										</P>
									</li>
									<li>
										<P>Fraud or fraudulent misrepresentation by WELOVENIGHT.</P>
									</li>
									<li>
										<P>
											Any other liability that cannot be excluded or limited
											under applicable law.
										</P>
									</li>
								</Ol>
								<P>
									This limitation of liability clause shall survive termination
									of these Purchase Conditions and shall apply to the maximum
									extent permitted by Spanish and European Union law.
								</P>
							</li>
						</Ol>
					</li>

					<li className="space-y-4">
						<H2>Consumer Rights</H2>
						<P>
							Nothing in this Refund Policy affects statutory consumer rights
							under Spanish and European law.
						</P>
						<P>
							For information on consumer rights, contact consumer protection
							authorities or visit:{` `}
							<A
								href="https://consumo.sede.gob.es/procedimientos/index/categoria/1420"
								target="_blank"
								rel="noopener noreferrer"
							>
								https://consumo.sede.gob.es/procedimientos/index/categoria/1420
							</A>
							.
						</P>
					</li>

					<li className="space-y-4">
						<H2>Indemnification</H2>
						<P>
							To the maximum extent permitted by applicable law, each Customer
							agrees to defend, indemnify, and hold harmless WELOVENIGHT, its
							affiliates, officers, directors, employees, agents, and
							representatives from and against any and all claims, damages,
							obligations, losses, liabilities, costs, or debt, and expenses
							(including but not limited to attorney's fees) arising from: (a)
							the Customer's use of the Platform; (b) the Customer's breach of
							these Purchase Conditions; (c) the Customer's violation of any
							third-party rights, including without limitation any copyright,
							property, or privacy right; (d) the Customer's conduct at any
							Event; (e) any false or misleading information provided by the
							Customer; or (f) any claim that the Customer's use of the Platform
							caused damage to a third party.
						</P>
					</li>

					<li className="space-y-4">
						<H2>Modification of Conditions</H2>
						<P>
							WELOVENIGHT reserves the right to modify these Purchase Conditions
							at any time. Changes will be effective immediately upon posting on
							the Platform. Customers are responsible for regularly reviewing
							these conditions. Continued use of the Platform after
							modifications constitutes acceptance of the revised conditions.
							For material changes, WELOVENIGHT will provide reasonable notice
							through the Platform or via email.
						</P>
					</li>

					<li className="space-y-4">
						<H2>Severability</H2>
						<P>
							If any provision of these Purchase Conditions is found to be
							invalid, illegal, or unenforceable by a court of competent
							jurisdiction, such provision shall be deemed severed from these
							conditions, and the remaining provisions shall continue in full
							force and effect to the maximum extent permitted by law.
						</P>
					</li>

					<li className="space-y-4">
						<H2>Waiver</H2>
						<P>
							No waiver by WELOVENIGHT of any term or condition set forth in
							these Purchase Conditions shall be deemed a further or continuing
							waiver of such term or condition or a waiver of any other term or
							condition, and any failure by WELOVENIGHT to assert a right or
							provision under these conditions shall not constitute a waiver of
							such right or provision.
						</P>
						<P>
							<Strong>Important:</Strong> Customers may purchase tickets with or
							without a registered account on the Platform. For information on
							account creation, management, and the benefits of having a
							registered account, please refer to the{` `}
							<A href="/registration-conditions">
								Registration and User Account Conditions
							</A>
							.
						</P>
					</li>

					<li className="space-y-4">
						<H2>Applicable Law and Jurisdiction</H2>
						<Ol>
							<li className="space-y-4">
								<H3>Applicable law</H3>
								<P>
									These Registration Conditions are governed by Spanish law.
								</P>
							</li>

							<li className="space-y-4">
								<H3>Jurisdiction for consumers</H3>
								<P>
									In the event of any dispute, the User that is a consumer will
									first attempt to resolve the matter amicably by contacting our
									customer service team.
								</P>
								<P>
									If no amicable solution is reached, and in accordance with
									applicable consumer protection laws, the consumer may refer
									the dispute to an officially recognized Alternative Dispute
									Resolution (ADR) entity in Spain.
								</P>
								<P>
									Furthermore, pursuant to Article 29 of the General Law for the
									Defence of Consumers and Users, any legal dispute shall be
									submitted to the courts of the consumer's place of residence.
								</P>
								<P>
									Customers can consult the official list of ADR entities in
									Spain on the website of the Spanish Consumer Affairs Authority
									at{` `}
									<A
										href="https://www.consumo.gob.es"
										target="_blank"
										rel="noopener noreferrer"
									>
										https://www.consumo.gob.es
									</A>
									.
								</P>
							</li>

							<li className="space-y-4">
								<H3>Jurisdiction for professionals</H3>
								<P>
									For Users without consumer status (Organisers, PRs,
									companies), disputes will be submitted to the exclusive
									jurisdiction of the Courts and Tribunals of Palma de Mallorca,
									Spain.
								</P>
							</li>
						</Ol>
					</li>
				</Ol>

				<div className="mt-8 space-y-2 border-t pt-8">
					<P className="mb-0">
						<Strong>Version:</Strong> 1.0
					</P>
					<P className="mb-0">
						<Strong>Effective date:</Strong> October 2025
					</P>
					<P>
						<Strong>Last reviewed:</Strong> October 2025
					</P>
				</div>
			</div>
		</Container>
	)
}
